"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { Chat } from "@/components/chat";
import { DEFAULT_CHAT_MODEL } from "@/lib/ai/models";
import { isAgentModelKey } from "@/lib/ai/agent-models";
import { generateUUID } from "@/lib/utils";
import { AppSidebar } from "@/components/app-sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { HomeShowcaseBuddyTour } from "@/components/buddy/HomeShowcaseBuddyTour";
import { OnboardingFinaleModal } from "@/components/onboarding/OnboardingFinaleModal";
import { AuthFlow } from "@/components/auth/auth-flow";
import { ReferralBanner } from "@/components/referral-banner";
import { useAuth } from "@/contexts/AuthContext";

// Utility function to get cookie value
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
}

export function LoggedInHome() {
  const searchParams = useSearchParams();
  const { user, emailVerified, checkingVerification } = useAuth();

  // Stabilize id, query and sourceNotebookId on first render - never recompute
  const [{ id, initialQuery, initialPrefill, sourceNotebookId, initialModelTier, initialModelKey, initialSkillRun, initialActiveSkill }] = useState(() => {
    const tier = searchParams.get("model_tier");
    const parsedModelTier: "basic" | "max" | undefined =
      tier === "max" || tier === "basic" ? tier : undefined;
    const modelParam = searchParams.get("model");
    const parsedModelKey = isAgentModelKey(modelParam) ? modelParam : undefined;
    // Skill launch (no-blob): `?skill=<id>&skillname=<name>` arms the chat bar
    // with an active skill instead of pasting the instruction into the message.
    const skillId = searchParams.get("skill");
    const skillName = searchParams.get("skillname");
    const activeSkill = skillId
      ? { id: skillId, name: skillName || "Skill" }
      : undefined;
    if (activeSkill) {
      // eslint-disable-next-line no-console
      console.log("[SKILL-LAUNCH] home read active skill from URL", activeSkill);
    }
    return {
      id: searchParams.get("id") || generateUUID(),
      initialQuery: searchParams.get("q"),
      initialPrefill: searchParams.get("prefill"),
      sourceNotebookId: searchParams.get("sourceNotebookId"),
      initialModelTier: parsedModelTier,
      // Skill launch params (?model=opus&skillrun=1).
      initialModelKey: parsedModelKey,
      initialSkillRun: searchParams.get("skillrun") === "1",
      initialActiveSkill: activeSkill,
    };
  });

  const [isCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const saved = localStorage.getItem("sidebar:state");
    return saved !== "true";
  });
  const modelIdFromCookie = getCookie("chat-model") || DEFAULT_CHAT_MODEL;
  const canAutoSubmitInitialQuery =
    emailVerified === true && !checkingVerification;

  const chatComponent = (
    <Chat
      key={id}
      id={id}
      initialMessages={[]}
      initialVisibilityType="private"
      isReadonly={false}
      // Reliability fix (July 2026): recovery was disabled on the home surface —
      // 37/37 blocked recoveries in the 6 days before this change were
      // stream.recovery_gated with auto_resume=False on first-message chats.
      // Same pattern as alerts/[id]/page.tsx: enabled only for signed-in users
      // (logged-out has nothing persisted server-side to resume).
      autoResume={Boolean(user?.uid)}
      section="chat"
      initialUserQuery={
        canAutoSubmitInitialQuery ? initialQuery || undefined : undefined
      }
      initialPrefill={initialPrefill || undefined}
      sourceNotebookId={sourceNotebookId || undefined}
      initialModelTier={initialModelTier}
      initialModelKey={initialModelKey}
      initialSkillRun={initialSkillRun}
      initialActiveSkill={initialActiveSkill}
    />
  );

  return (
    <div className="flex flex-col h-dvh">
      <ReferralBanner />
      <SidebarProvider defaultOpen={!isCollapsed} className="flex-1 min-h-0">
        <AppSidebar />
        <HomeShowcaseBuddyTour />
        {/* Closing scene of the cross-page onboarding chain. Only
            renders content when the URL is `?onboarding_home=finale`,
            otherwise it's an inert mount. */}
        <OnboardingFinaleModal />
        {/* One-time Terminal launch announcement (per user, per browser). */}
        <SidebarInset>
          <AuthFlow>{chatComponent}</AuthFlow>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
